import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { technicalEvaluationApi } from "../../../api/technicalEvaluation";
import { notifications } from "@mantine/notifications";

export const useTechnicalEvaluations = (tenderId: string) => {
  return useQuery({
    queryKey: ['technicalEvaluations', tenderId],
    queryFn: async () => {
      const res = await technicalEvaluationApi.listEvaluations(tenderId);
      return res.data;
    },
    enabled: !!tenderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTechnicalEvaluationById = (tenderId: string, evaluationId: string) => {
  return useQuery({
    queryKey: ['technicalEvaluation', tenderId, evaluationId],
    queryFn: async () => {
      const res = await technicalEvaluationApi.getEvaluationById(tenderId, evaluationId);
      return res.data;
    },
    enabled: !!tenderId && !!evaluationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateTechnicalEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenderId, data }: { tenderId: string; data: any }) => {
      const res = await technicalEvaluationApi.createEvaluation(tenderId, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluations', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Technical evaluation created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create technical evaluation",
        color: "red",
      });
    },
  });
};

export const useCreateBulkTechnicalEvaluations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenderId: string) => {
      const res = await technicalEvaluationApi.createBulkEvaluations(tenderId);
      return res.data;
    },
    onSuccess: (_data, tenderId) => {
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluations', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      notifications.show({
        title: "Success",
        message: "Technical evaluations created",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create technical evaluations",
        color: "red",
      });
    },
  });
};

export const useUpdateTechnicalEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenderId, evaluationId, data }: { tenderId: string; evaluationId: string; data: any }) => {
      const res = await technicalEvaluationApi.updateEvaluation(tenderId, evaluationId, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluations', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluation', variables.tenderId, variables.evaluationId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Technical evaluation updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update technical evaluation",
        color: "red",
      });
    },
  });
};

export const useUpdateDocumentReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenderId, evaluationId, reviewId, data }: { tenderId: string; evaluationId: string; reviewId: string; data: any }) => {
      const res = await technicalEvaluationApi.updateDocumentReview(tenderId, evaluationId, reviewId, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluations', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['technicalEvaluation', variables.tenderId, variables.evaluationId] });
      notifications.show({
        title: "Success",
        message: "Document review updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update document review",
        color: "red",
      });
    },
  });
};
